import React, { useState, useEffect, useRef } from 'react';
import { 
    CheckCircle, AlertTriangle, XCircle, TrendingUp, Star, 
    Upload, Activity, AlertOctagon, ArrowRight, Copy, 
    FileText, Zap, ChevronRight, Sparkles, BookOpen, Loader2, Download, MonitorPlay, Smartphone, Clapperboard
} from 'lucide-react';
import { AppButton, BlackboxLoaderIcon, AppCard } from '../../components/SharedUI';
import { generateGreenlightCoverageLLM } from '../../services/aiService';

// --- PDF EXTRACTION ENGINE (For reading scripts) ---
const extractScriptText = async (file) => {
    if (file.type === "application/pdf") {
        try {
            if (!window.pdfjsLib) {
                const script = document.createElement('script');
                script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
                document.head.appendChild(script);
                await new Promise(resolve => script.onload = resolve);
            }
            window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
            const arrayBuffer = await file.arrayBuffer();
            const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;
            let fullText = "";
            const maxPages = Math.min(pdf.numPages, 40); 
            for (let i = 1; i <= maxPages; i++) {
                const page = await pdf.getPage(i);
                const textContent = await page.getTextContent();
                const pageText = textContent.items.map(item => item.str).join(' ');
                fullText += `\n--- PAGE ${i} ---\n` + pageText;
            }
            return fullText;
        } catch (error) {
            console.error("PDF Error:", error);
            throw new Error("Could not parse PDF. Please try a text file.");
        }
    } else {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.onerror = reject;
            reader.readAsText(file);
        });
    }
};

// --- SUB-COMPONENTS ---

const ScoreGauge = ({ score, label, color }) => (
    <div className="flex flex-col items-center justify-center p-6 bg-zinc-950 border border-zinc-800 rounded-2xl relative overflow-hidden group">
        <div className={`absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500 bg-gradient-to-br from-transparent to-${color.split('-')[1]}-500`} />
        <div className="relative w-28 h-28 flex items-center justify-center">
            <svg className="w-full h-full transform -rotate-90 drop-shadow-lg">
                <circle cx="56" cy="56" r="48" className="text-zinc-900" strokeWidth="8" fill="transparent" stroke="currentColor" />
                <circle cx="56" cy="56" r="48" className={color} strokeWidth="8" fill="transparent" strokeDasharray={301.6} strokeDashoffset={301.6 - (301.6 * score) / 100} strokeLinecap="round" stroke="currentColor" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-black text-white font-poppins">{score}</span>
            </div>
        </div>
        <span className="mt-3 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</span>
    </div>
);

const PitchSection = ({ title, content, icon: Icon, color = "text-purple-400", onCopy }) => (
    <div className="group relative bg-zinc-900/50 border border-zinc-800 p-6 rounded-xl hover:border-zinc-700 transition-colors">
        <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg bg-zinc-950 border border-zinc-800 ${color}`}>
                    <Icon size={18} />
                </div>
                <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-widest">{title}</h4>
            </div>
            <button onClick={() => onCopy(content)} className="p-2 text-zinc-600 hover:text-white transition-colors opacity-0 group-hover:opacity-100" title="Copy to clipboard">
                <Copy size={14} />
            </button>
        </div>
        <p className="text-zinc-200 text-sm leading-relaxed font-light whitespace-pre-wrap font-poppins">{content}</p>
    </div>
);

const GreenlightCoverageTab = ({ filmData, saveFilmData, setNotification, credits, deductCredit }) => {
    const [coverage, setCoverage] = useState(filmData?.greenlight_coverage || null);
    const [loading, setLoading] = useState(false);
    const [processingFile, setProcessingFile] = useState(false);
    const [scriptContent, setScriptContent] = useState(filmData?.script || '');
    const [analysisError, setAnalysisError] = useState(null); // Track analysis errors
    const [projectFormat, setProjectFormat] = useState(filmData?.projectFormat || 'Feature Film');
    const fileInputRef = useRef(null);

  useEffect(() => {
        if (filmData?.greenlight_coverage) setCoverage(filmData.greenlight_coverage);
        if (filmData?.script) setScriptContent(filmData.script);
        if (filmData?.projectFormat) setProjectFormat(filmData.projectFormat); // <--- NEW LINE
    }, [filmData]);

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        setProcessingFile(true);
        setNotification({ type: 'info', message: 'Extracting script text...' });
        try {
            const text = await extractScriptText(file);
            if (text.length < 100) throw new Error("Script file appears empty or unreadable.");
            setScriptContent(text);
            await saveFilmData({ ...filmData, script: text });
            setNotification({ type: 'success', message: 'Script successfully digitized.' });
        } catch (error) {
            console.error(error);
            setNotification({ type: 'error', message: 'Failed to read file. If using PDF, ensure it is text-based.' });
        } finally {
            setProcessingFile(false);
        }
    };

   const runCoverage = async () => {
        if (!scriptContent) return setNotification({ type: 'error', message: 'Please upload a script first.' });
        setLoading(true);
        setAnalysisError(null);
        try {
            // --- UPDATED CALL ---
            const result = await generateGreenlightCoverageLLM(scriptContent, filmData, credits, deductCredit, setNotification, projectFormat);
            
            setCoverage(result);
            // Save the format so we remember it next time
            await saveFilmData({ ...filmData, greenlight_coverage: result, projectFormat }); 
        } catch (e) {
            console.error(e);
            if (e.message && (e.message.includes('Content Safety Block') || e.blockReason)) {
                setAnalysisError({
                    type: 'content_safety',
                    message: e.message,
                    blockReason: e.blockReason,
                    safetyRatings: e.safetyRatings
                });
            } else {
                setAnalysisError({
                    type: 'general',
                    message: e.message || 'Analysis failed. Please try again.'
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        setNotification({ type: 'success', message: 'Copied to clipboard' });
    };

    // --- PDF EXPORT ENGINE ---
    const handleExportPDF = () => {
        try {
            if (!window.jspdf || !coverage) {
                setNotification({ type: 'error', message: 'PDF generator not ready or no data.' });
                return;
            }
            
            const { jsPDF } = window.jspdf;
            const doc = new jsPDF('p', 'pt', 'a4');
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 40;
            let y = margin;

            const COLORS = {
                GREEN: [34, 197, 94],
                YELLOW: [234, 179, 8],
                RED: [239, 68, 68],
                DARK: [24, 24, 27],
                GRAY: [113, 113, 122],
                PURPLE: [147, 51, 234],
                BG: [250, 250, 250]
            };

            const getVerdictColor = (v) => {
                if (v === 'Greenlight') return COLORS.GREEN;
                if (v === 'Yellow Light') return COLORS.YELLOW;
                return COLORS.RED;
            };

            const verdictColor = getVerdictColor(coverage.verdict);

            const checkPageBreak = (needed) => {
                if (y + needed > pageHeight - margin) {
                    doc.addPage();
                    y = margin;
                    return true;
                }
                return false;
            };

            const drawSectionHeader = (title) => {
                checkPageBreak(40);
                y += 20;
                doc.setFont("helvetica", "bold");
                doc.setFontSize(12);
                doc.setTextColor(...COLORS.GRAY);
                doc.text(title.toUpperCase(), margin, y);
                doc.setDrawColor(200, 200, 200);
                doc.line(margin, y + 8, pageWidth - margin, y + 8);
                y += 25;
            };

            // --- PAGE 1: THE EXECUTIVE PITCH ---

            // 1. Header (Dark block)
            doc.setFillColor(...COLORS.DARK);
            doc.rect(0, 0, pageWidth, 120, 'F');
            
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(24);
            doc.setFont("helvetica", "bold");
            doc.text("EXECUTIVE COVERAGE", margin, 50);
            
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(200, 200, 200);
            doc.text(`PROJECT: ${filmData.title?.toUpperCase() || "UNTITLED"}`, margin, 75);
            doc.text(`DATE: ${new Date().toLocaleDateString()}`, pageWidth - margin - 80, 75);

            y = 150;

            // 2. The Verdict Banner
            doc.setFillColor(...verdictColor);
            doc.roundedRect(margin, y, pageWidth - (margin * 2), 60, 4, 4, 'F');
            
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(10);
            doc.setFont("helvetica", "bold");
            doc.text("FINAL VERDICT", margin + 20, y + 25);
            
            doc.setFontSize(28);
            doc.text(coverage.verdict.toUpperCase(), margin + 20, y + 50);
            
            // Score bubbles in banner
            const drawScoreBubble = (label, score, xOffset) => {
                doc.setFillColor(0, 0, 0, 0.2); // Semi-transparent black
                doc.roundedRect(pageWidth - margin - xOffset, y + 10, 80, 40, 4, 4, 'F');
                doc.setFontSize(8);
                doc.text(label, pageWidth - margin - xOffset + 40, y + 22, { align: 'center' });
                doc.setFontSize(16);
                doc.text(score.toString(), pageWidth - margin - xOffset + 40, y + 38, { align: 'center' });
            };
            drawScoreBubble("CREATIVE", coverage.creative_score, 90);
            drawScoreBubble("COMMERCIAL", coverage.commercial_score, 180);

            y += 90;

            // 3. Executive Summary
            doc.setFont("helvetica", "bold");
            doc.setFontSize(12);
            doc.setTextColor(...COLORS.DARK);
            doc.text("EXECUTIVE SUMMARY", margin, y);
            y += 15;
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            const summaryLines = doc.splitTextToSize(coverage.the_verdict_summary, pageWidth - (margin * 2));
            doc.text(summaryLines, margin, y);
            y += summaryLines.length * 14 + 20;

            // 4. Pitch Packet Grid
            if (coverage.financier_pitch) {
                drawSectionHeader("Financier Pitch Packet");
                
                const renderBox = (title, content) => {
                    checkPageBreak(100);
                    doc.setFillColor(...COLORS.BG);
                    doc.roundedRect(margin, y, pageWidth - (margin * 2), 10, 4, 4, 'F'); // Placeholder height, calculate real height
                    let boxY = y + 15;
                    
                    doc.setFont("helvetica", "bold");
                    doc.setFontSize(10);
                    doc.setTextColor(...COLORS.PURPLE);
                    doc.text(title.toUpperCase(), margin + 15, boxY);
                    boxY += 15;
                    
                    doc.setFont("helvetica", "normal");
                    doc.setTextColor(...COLORS.DARK);
                    const lines = doc.splitTextToSize(content || "N/A", pageWidth - (margin * 2) - 30);
                    doc.text(lines, margin + 15, boxY);
                    
                    const height = (lines.length * 14) + 40;
                    doc.roundedRect(margin, y, pageWidth - (margin * 2), height, 4, 4, 'S'); // Draw border
                    y += height + 15;
                };

                renderBox("Logline", coverage.financier_pitch.logline);
                renderBox("Thematic Core", coverage.financier_pitch.theme);
                renderBox("Tone & Comps", coverage.financier_pitch.tone);
            }

            // --- PAGE 2: THE ANALYSIS ---
            doc.addPage();
            y = margin;
            
            // Header 2
            doc.setFontSize(18);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(...COLORS.DARK);
            doc.text("FORENSIC ANALYSIS", margin, y);
            y += 30;

            // 5. SWOT (Two columns)
            doc.setFontSize(12);
            doc.setTextColor(...COLORS.GREEN);
            doc.text("MAJOR STRENGTHS", margin, y);
            doc.setTextColor(...COLORS.RED);
            doc.text("CRITICAL WEAKNESSES", pageWidth / 2 + 10, y);
            y += 15;

            doc.setFontSize(10);
            doc.setTextColor(...COLORS.DARK);
            doc.setFont("helvetica", "normal");

            const startY = y;
            let leftY = startY;
            let rightY = startY;

            coverage.strengths?.forEach(s => {
                const lines = doc.splitTextToSize(`• ${s}`, (pageWidth / 2) - margin - 20);
                doc.text(lines, margin, leftY);
                leftY += lines.length * 14 + 5;
            });

            coverage.weaknesses?.forEach(w => {
                const lines = doc.splitTextToSize(`• ${w}`, (pageWidth / 2) - margin - 20);
                doc.text(lines, pageWidth / 2 + 10, rightY);
                rightY += lines.length * 14 + 5;
            });

            y = Math.max(leftY, rightY) + 30;

            // 6. Development Notes
            drawSectionHeader("Development Notes (The Fix)");
            coverage.major_issues?.forEach(issue => {
                checkPageBreak(50);
                doc.setFont("helvetica", "bold");
                doc.setTextColor(...COLORS.RED);
                doc.text(issue.issue.toUpperCase(), margin, y);
                y += 12;
                
                doc.setFont("helvetica", "normal");
                doc.setTextColor(...COLORS.DARK);
                const lines = doc.splitTextToSize(`FIX: ${issue.fix}`, pageWidth - (margin * 2));
                doc.text(lines, margin, y);
                y += lines.length * 14 + 10;
            });

            // 7. Casting
            if (coverage.star_power_needed) {
                drawSectionHeader("Casting Strategy");
                doc.setFont("helvetica", "italic");
                doc.setTextColor(...COLORS.DARK);
                const verdictLines = doc.splitTextToSize(`"${coverage.star_power_needed.verdict}"`, pageWidth - (margin * 2));
                doc.text(verdictLines, margin, y);
                y += verdictLines.length * 14 + 15;

                doc.setFont("helvetica", "bold");
                doc.setTextColor(...COLORS.PURPLE);
                const names = coverage.star_power_needed.suggested_names?.join("  |  ") || "No specific suggestions";
                doc.text(names, margin, y);
            }

            // Footer
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(...COLORS.GRAY);
                doc.text(`BLACKBOX Greenlight Intelligence  |  Confidential  |  Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 20, { align: 'center' });
            }

            doc.save(`${filmData.title?.replace(/\s+/g, '_')}_Greenlight_Coverage.pdf`);
            setNotification({ type: 'success', message: 'Greenlight Dossier exported.' });

        } catch (error) {
            console.error("PDF Generation Error:", error);
            setNotification({ type: 'error', message: 'Failed to generate PDF.' });
        }
    };

    const getVerdictStyle = (v) => {
        if (!v) return {};
        if (v === 'Greenlight') return { color: 'text-green-400', border: 'border-green-500/50', bg: 'bg-green-950/20', shadow: 'shadow-green-900/20' };
        if (v === 'Yellow Light') return { color: 'text-yellow-400', border: 'border-yellow-500/50', bg: 'bg-yellow-950/20', shadow: 'shadow-yellow-900/20' };
        return { color: 'text-red-500', border: 'border-red-500/50', bg: 'bg-red-950/20', shadow: 'shadow-red-900/20' };
    };

    const verdictStyle = coverage ? getVerdictStyle(coverage.verdict) : {};

    const FormatButton = ({ type, icon: Icon, label }) => (
        <button
            onClick={() => setProjectFormat(type)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${
                projectFormat === type 
                ? 'bg-purple-600 text-white border-purple-500 shadow-lg shadow-purple-900/50' 
                : 'bg-zinc-900 text-zinc-400 border-zinc-800 hover:text-white hover:border-zinc-700'
            }`}
        >
            <Icon size={14} />
            {label}
        </button>
    );

    return (
        <div className="h-full flex flex-col pb-24">
            {/* HEADER */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 pb-6 border-b border-zinc-800 gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-zinc-900 rounded-xl flex items-center justify-center border border-zinc-800">
                        <Activity size={24} className="text-white" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-white font-poppins tracking-tight">Greenlight Coverage</h2>
                       <div className="flex items-center gap-3 mt-2">
                            <p className="text-[10px] text-zinc-500 uppercase tracking-widest font-bold mt-1">Format:</p>
                            <div className="flex gap-2">
                                <FormatButton type="Feature Film" icon={Clapperboard} label="Feature" />
                                <FormatButton type="TV Pilot" icon={MonitorPlay} label="TV Pilot" />
                                <FormatButton type="Vertical Series" icon={Smartphone} label="Vertical" />
                            </div>
                        </div>
                        <p className="text-xs text-zinc-500 uppercase tracking-widest">AI Executive Assessment & Script Forensics</p>
                    </div>
                </div>

                {!scriptContent ? (
                    <button 
                        onClick={() => fileInputRef.current?.click()}
                        disabled={processingFile}
                        className="group relative px-6 py-3 bg-zinc-100 hover:bg-white text-black font-bold rounded-xl transition-all flex items-center gap-2 disabled:opacity-50"
                    >
                        {processingFile ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} />} 
                        {processingFile ? "Digitizing..." : "Upload Script (PDF/TXT)"}
                        <input ref={fileInputRef} type="file" accept=".txt,.pdf,.fountain" onChange={handleFileUpload} className="hidden" />
                    </button>
                ) : (
                    <div className="flex gap-3">
                        <div className="px-4 py-2 bg-zinc-900 rounded-lg border border-zinc-800 flex items-center gap-2 text-zinc-400 text-xs font-mono">
                            <CheckCircle size={12} className="text-green-500" /> SCRIPT LOADED
                        </div>
                         <button 
                            onClick={() => fileInputRef.current?.click()}
                            className="p-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded-lg text-zinc-400 hover:text-white"
                            title="Replace Script"
                        >
                            <Upload size={14} />
                            <input ref={fileInputRef} type="file" accept=".txt,.pdf,.fountain" onChange={handleFileUpload} className="hidden" />
                        </button>
                        {coverage && (
                            <button 
                                onClick={handleExportPDF}
                                className="p-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded-lg text-zinc-400 hover:text-white"
                                title="Export PDF Dossier"
                            >
                                <Download size={14} />
                            </button>
                        )}
                        <AppButton onClick={runCoverage} disabled={loading || processingFile} icon={Zap} className="bg-purple-600 hover:bg-purple-500 text-white border-0">
                            {loading ? 'Analyzing...' : `Analyze ${projectFormat} (8)`}
                        </AppButton>
                    </div>
                )}
            </div>

            {loading ? (
                <div className="flex-1 flex flex-col items-center justify-center p-12">
                    <BlackboxLoaderIcon className="w-20 h-20 animate-pulse text-purple-500" />
                    <h3 className="text-xl font-bold text-white mt-8 mb-2 font-poppins">
                        Analyzing {projectFormat}...
                    </h3>
                    <p className="text-zinc-500 text-sm font-mono">
                        {projectFormat === 'TV Pilot' ? 'Checking season potential & world-building...' : 
                         projectFormat === 'Vertical Series' ? 'Measuring retention hooks & viral pacing...' : 
                         'Analyzing theatrical pacing & commercial viability...'}
                    </p>
                </div>
            ) : analysisError ? (
                <div className="flex-1 flex flex-col items-center justify-center p-8">
                    <div className="max-w-3xl w-full bg-red-950/20 border border-red-500/50 rounded-3xl p-8">
                        <div className="flex items-start gap-4 mb-6">
                            <div className="w-12 h-12 bg-red-900/50 rounded-xl flex items-center justify-center border border-red-500/30 flex-shrink-0">
                                <AlertOctagon className="w-6 h-6 text-red-400" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-xl font-bold text-red-400 font-poppins mb-2">
                                    {analysisError.type === 'content_safety' ? 'Content Safety Block' : 'Analysis Failed'}
                                </h3>
                                <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap">
                                    {analysisError.message}
                                </p>
                            </div>
                        </div>

                        {analysisError.safetyRatings && analysisError.safetyRatings.length > 0 && (
                            <div className="bg-zinc-900/50 rounded-xl p-4 mb-6">
                                <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-3">Flagged Content Categories:</h4>
                                <div className="space-y-2">
                                    {analysisError.safetyRatings
                                        .filter(r => r.probability !== 'NEGLIGIBLE' && r.probability !== 'LOW')
                                        .map((rating, i) => (
                                            <div key={i} className="flex items-center justify-between text-sm">
                                                <span className="text-zinc-300">
                                                    {rating.category.replace('HARM_CATEGORY_', '').replace('_', ' ')}
                                                </span>
                                                <span className={`px-2 py-1 rounded text-xs font-bold ${
                                                    rating.probability === 'HIGH' ? 'bg-red-900/50 text-red-400' :
                                                    rating.probability === 'MEDIUM' ? 'bg-yellow-900/50 text-yellow-400' :
                                                    'bg-zinc-800 text-zinc-400'
                                                }`}>
                                                    {rating.probability}
                                                </span>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )}

                        <div className="bg-zinc-900/50 rounded-xl p-4 mb-6">
                            <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-2">What This Means:</h4>
                            <p className="text-zinc-300 text-sm leading-relaxed">
                                Screenplays often contain mature themes (violence, profanity, adult situations) that are flagged by AI safety systems designed to prevent harmful content. 
                                This is a limitation of the AI, not your screenplay. The system cannot distinguish between artistic depiction and promotion of harmful content.
                            </p>
                        </div>

                        <div className="bg-purple-900/20 border border-purple-500/30 rounded-xl p-4">
                            <h4 className="text-xs font-bold text-purple-400 uppercase tracking-widest mb-2">Possible Solutions:</h4>
                            <ul className="space-y-2 text-sm text-zinc-300">
                                <li className="flex gap-2">
                                    <ChevronRight size={16} className="text-purple-400 flex-shrink-0 mt-0.5" />
                                    <span>Try analyzing a different section of your script that may be less intense</span>
                                </li>
                                <li className="flex gap-2">
                                    <ChevronRight size={16} className="text-purple-400 flex-shrink-0 mt-0.5" />
                                    <span>Focus on sections with more dialogue and less action/violence</span>
                                </li>
                                <li className="flex gap-2">
                                    <ChevronRight size={16} className="text-purple-400 flex-shrink-0 mt-0.5" />
                                    <span>Contact support if you believe this is incorrectly blocking legitimate content</span>
                                </li>
                            </ul>
                        </div>

                        <div className="mt-6 flex gap-3">
                            <AppButton 
                                onClick={() => setAnalysisError(null)} 
                                className="bg-zinc-800 hover:bg-zinc-700 text-white border-0 flex-1"
                            >
                                Dismiss
                            </AppButton>
                            <AppButton 
                                onClick={runCoverage} 
                                disabled={loading}
                                icon={Zap}
                                className="bg-purple-600 hover:bg-purple-500 text-white border-0 flex-1"
                            >
                                Try Again
                            </AppButton>
                        </div>
                    </div>
                </div>
            ) : !coverage ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center p-8 bg-zinc-900/30 rounded-3xl border border-zinc-800 border-dashed">
                    <div className="w-20 h-20 bg-zinc-900 rounded-full flex items-center justify-center mb-6 border border-zinc-800">
                        <AlertOctagon className="w-8 h-8 text-zinc-600" />
                    </div>
                    <h3 className="text-xl font-bold text-white font-poppins">No Coverage Report</h3>
                    <p className="text-zinc-500 max-w-md mt-2 mb-6 text-sm">Upload your script (PDF supported) to receive a brutal, honest assessment of your project's commercial and creative viability.</p>
                </div>
            ) : (
                <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    
                    {/* 1. THE VERDICT BANNER */}
                    <div className={`p-8 md:p-10 rounded-3xl border ${verdictStyle.border} ${verdictStyle.bg} shadow-2xl ${verdictStyle.shadow} relative overflow-hidden`}>
                        <div className="relative z-10 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8">
                            <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                    <span className="w-2 h-2 rounded-full bg-current animate-pulse"></span>
                                    <p className={`text-xs font-bold uppercase tracking-[0.2em] ${verdictStyle.color} opacity-80`}>
                                        Executive Verdict ({projectFormat})
                                    </p>
                                </div>
                                <h1 className={`text-5xl md:text-7xl font-black uppercase italic tracking-tighter ${verdictStyle.color}`}>
                                    {coverage.verdict}
                                </h1>
                            </div>
                            
                            <div className="hidden lg:block w-px h-24 bg-current opacity-10"></div>
                            
                            <div className="flex-1 max-w-2xl">
                                <p className="text-lg md:text-xl font-medium text-zinc-100 leading-relaxed font-poppins">
                                    "{coverage.the_verdict_summary}"
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* 2. FINANCIER PITCH PACKET */}
                    {coverage.financier_pitch && (
                        <div>
                             <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-bold text-white font-poppins flex items-center gap-2">
                                    <Sparkles className="text-purple-400" size={18} />
                                    Creative Overview
                                </h3>
                                <span className="text-[10px] uppercase font-bold text-zinc-600 tracking-widest bg-zinc-900 px-3 py-1 rounded-full border border-zinc-800">Copy Ready</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <PitchSection 
                                    title="Logline" 
                                    content={coverage.financier_pitch.logline} 
                                    icon={FileText} 
                                    color="text-blue-400"
                                    onCopy={handleCopy}
                                />
                                <PitchSection 
                                    title="Thematic Core" 
                                    content={coverage.financier_pitch.theme} 
                                    icon={BookOpen} 
                                    color="text-pink-400"
                                    onCopy={handleCopy}
                                />
                                <PitchSection 
                                    title="Tone / Comps" 
                                    content={coverage.financier_pitch.tone} 
                                    icon={Zap} 
                                    color="text-yellow-400"
                                    onCopy={handleCopy}
                                />
                            </div>
                        </div>
                    )}

                    {/* 3. METRICS & GRAPHS */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <ScoreGauge score={coverage.commercial_score} label="Commercial Viability" color="text-green-400" />
                        <ScoreGauge score={coverage.creative_score} label="Creative Execution" color="text-purple-400" />
                        
                        <div className="md:col-span-2 bg-zinc-950 border border-zinc-800 p-6 rounded-2xl flex flex-col justify-center">
                            <h4 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-6 flex items-center gap-2">
                                <Star size={14} className="text-yellow-500" /> Character Forensics
                            </h4>
                            <div className="space-y-6">
                                <div>
                                    <div className="flex justify-between text-sm text-zinc-300 mb-2 font-medium">
                                        <span>Protagonist Strength</span>
                                        <span className="font-mono text-white">{coverage.character_pass?.protagonist_rating}/10</span>
                                    </div>
                                    <div className="h-1.5 bg-zinc-900 rounded-full overflow-hidden">
                                        <div className="h-full bg-blue-500 rounded-full" style={{ width: `${coverage.character_pass?.protagonist_rating * 10}%` }}></div>
                                    </div>
                                </div>
                                <div>
                                    <div className="flex justify-between text-sm text-zinc-300 mb-2 font-medium">
                                        <span>Antagonist Strength</span>
                                        <span className="font-mono text-white">{coverage.character_pass?.antagonist_rating}/10</span>
                                    </div>
                                    <div className="h-1.5 bg-zinc-900 rounded-full overflow-hidden">
                                        <div className="h-full bg-red-500 rounded-full" style={{ width: `${coverage.character_pass?.antagonist_rating * 10}%` }}></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 4. THE FIX LIST */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                         {/* Strengths & Weaknesses */}
                        <div className="space-y-4">
                             <div className="bg-zinc-900/30 border-l-2 border-green-500 p-5 rounded-r-xl">
                                <h4 className="text-xs font-bold text-green-500 uppercase tracking-widest mb-3">Major Strengths</h4>
                                <ul className="space-y-2">
                                    {coverage.strengths?.map((s, i) => (
                                        <li key={i} className="flex gap-3 text-zinc-300 text-sm">
                                            <CheckCircle size={14} className="text-green-500/50 flex-shrink-0 mt-0.5" />
                                            {s}
                                        </li>
                                    ))}
                                </ul>
                             </div>
                             <div className="bg-zinc-900/30 border-l-2 border-red-500 p-5 rounded-r-xl">
                                <h4 className="text-xs font-bold text-red-500 uppercase tracking-widest mb-3">Critical Weaknesses</h4>
                                <ul className="space-y-2">
                                    {coverage.weaknesses?.map((w, i) => (
                                        <li key={i} className="flex gap-3 text-zinc-300 text-sm">
                                            <XCircle size={14} className="text-red-500/50 flex-shrink-0 mt-0.5" />
                                            {w}
                                        </li>
                                    ))}
                                </ul>
                             </div>
                        </div>

                        {/* Critical Fixes */}
                        <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6">
                            <h3 className="text-sm font-bold text-white uppercase tracking-widest mb-6 flex items-center gap-2">
                                <Activity size={16} className="text-purple-500" /> Development Notes
                            </h3>
                            <div className="space-y-3">
                                {coverage.major_issues?.map((issue, i) => (
                                    <div key={i} className="bg-zinc-900/50 p-4 rounded-xl border border-zinc-800/50 hover:border-zinc-700 transition-colors">
                                        <p className="text-red-400 font-bold text-xs uppercase mb-1">{issue.issue}</p>
                                        <div className="flex gap-3">
                                            <ArrowRight size={14} className="mt-1 flex-shrink-0 text-zinc-600" />
                                            <p className="text-zinc-300 text-sm leading-snug">{issue.fix}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    
                    {/* 5. TALENT SUGGESTIONS */}
                    <div className="bg-gradient-to-r from-purple-900/10 to-transparent border border-purple-500/20 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-6">
                        <div>
                            <h4 className="text-purple-400 font-bold text-sm uppercase tracking-widest mb-1">Casting Strategy</h4>
                            <p className="text-zinc-300 text-sm italic">"{coverage.star_power_needed?.verdict}"</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {coverage.star_power_needed?.suggested_names?.map((name, i) => (
                                <span key={i} className="px-4 py-2 bg-purple-500/10 text-purple-200 border border-purple-500/30 rounded-lg text-xs font-bold shadow-lg shadow-purple-500/10">
                                    {name}
                                </span>
                            ))}
                        </div>
                    </div>

                </div>
            )}
        </div>
    );
};

export default GreenlightCoverageTab;